/**
 * The founder preview's own reload survival.
 *
 * The public investor path leans on the server: every turn is already a row,
 * so the client only needs to remember a session id and ask for it back. The
 * founder preview writes none of that on purpose — no turn row, no
 * conversation, no events, so a founder can read without limit and without
 * appearing in their own analytics. There is nothing on the server to ask
 * for, so this stores the turn itself, in this browser only, the moment it
 * finishes.
 */

import type { ProfileChatHistoryTurn } from './chat-history'

interface StoredLocalHistory {
  chatSessionId: string
  turns: ProfileChatHistoryTurn[]
}

function storageKeyFor(key: string): string {
  return `ask-profile-local-history:${key}`
}

/** Whatever this browser remembers of the founder's own chat, or null. */
export function readLocalChatHistory(key: string): StoredLocalHistory | null {
  try {
    const raw = localStorage.getItem(storageKeyFor(key))
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    if (!isStoredLocalHistory(parsed)) return null
    return parsed
  } catch {
    // Malformed JSON, a private window, or storage blocked entirely — starts
    // fresh, same as a first-time visit.
    return null
  }
}

/** Appends one finished turn, creating the record on the first one. */
export function appendLocalChatHistoryTurn(
  key: string,
  chatSessionId: string,
  turn: ProfileChatHistoryTurn
): void {
  try {
    const existing = readLocalChatHistory(key)
    const turns =
      existing && existing.chatSessionId === chatSessionId ? [...existing.turns, turn] : [turn]
    localStorage.setItem(storageKeyFor(key), JSON.stringify({ chatSessionId, turns }))
  } catch {
    // Best-effort: a founder who cannot persist locally simply sees the
    // reload behaviour they always have, not a broken page.
  }
}

export function clearLocalChatHistory(key: string): void {
  try {
    localStorage.removeItem(storageKeyFor(key))
  } catch {
    // Ignored, matching the write side.
  }
}

function isStoredLocalHistory(value: unknown): value is StoredLocalHistory {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Partial<StoredLocalHistory>
  return typeof candidate.chatSessionId === 'string' && Array.isArray(candidate.turns)
}
